// AuthorBubble — your message in the thread (pen "Chat – every entry" §1):
// the bubble, folded after 6 lines with "Show all · N lines" (unfolds in
// place; the full text always stays in the DOM), the chips of what you
// pointed at, and, when the server never got it, "Not sent. Try again".
//
// The fold is view geometry: the line count is measured after layout, and
// `expanded` is view-ephemeral (survives no reload, carries no domain
// meaning).

import { Trans, useLingui } from "@lingui/react/macro";
import {
    type ReactNode,
    type RefObject,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { Icon } from "../../Icon/Icon.js";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden.js";
import "./AuthorBubble.css";

// A message folds when it runs past this many lines (a layout contract).
const FOLD_LINES = 6;

// The bubble's laid-out line count (0 until measured).
const useLineCount = (
    ref: RefObject<HTMLDivElement | null>,
    text: string,
): number => {
    const [lines, setLines] = useState(0);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `text` is the content signal; the body reads only the ref.
    useLayoutEffect(() => {
        const el = ref.current;
        if (el === null) return;
        const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight);
        if (!(lineHeight > 0)) return;
        setLines(Math.round(el.scrollHeight / lineHeight));
    }, [text]);
    return lines;
};

export type UnsentState = {
    // Absent when sending again cannot help (or is not wired here).
    readonly onTryAgain?: () => void;
};

export const AuthorBubble = ({
    text,
    body,
    chips,
    unsent,
}: {
    // The plain text (measured; the source of the line count).
    readonly text: string;
    // The rendered text (mentions as chips inline).
    readonly body: ReactNode;
    // What the message pointed at, as chips under the bubble.
    readonly chips?: ReactNode;
    readonly unsent?: UnsentState;
}): ReactNode => {
    const { t } = useLingui();
    const ref = useRef<HTMLDivElement | null>(null);
    const lines = useLineCount(ref, text);
    const [expanded, setExpanded] = useState(false);
    const folds = lines > FOLD_LINES;
    const clipped = folds && !expanded;
    return (
        <div
            className={`bilda-author${unsent === undefined ? "" : " bilda-author--unsent"}`}
        >
            {chips}
            <div className="bilda-author__bubble">
                <VisuallyHidden>
                    <Trans>You said:</Trans>{" "}
                </VisuallyHidden>
                <div
                    ref={ref}
                    className={`bilda-author__text${clipped ? " bilda-author__text--clipped" : ""}`}
                >
                    {body}
                </div>
                {folds ? (
                    <button
                        type="button"
                        className="bilda-author__fold"
                        aria-expanded={expanded}
                        onClick={() => setExpanded((open) => !open)}
                    >
                        {expanded ? t`Show less` : t`Show all · ${lines} lines`}
                        <Icon
                            name={expanded ? "chevron-up" : "chevron-down"}
                            className="bilda-author__fold-icon"
                        />
                    </button>
                ) : null}
            </div>
            {unsent === undefined ? null : (
                <div className="bilda-author__unsent" role="status">
                    <Icon name="alert" className="bilda-author__unsent-icon" />
                    <span>
                        <Trans>Not sent.</Trans>
                    </span>
                    {unsent.onTryAgain === undefined ? null : (
                        <button
                            type="button"
                            className="bilda-author__retry"
                            onClick={unsent.onTryAgain}
                        >
                            <Trans>Try again</Trans>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
